import { AttachmentModel, WebLinkModel } from '@/api/feedbackPortal/queries';
import { Query } from '@/_core/cqrs/types/queries';
import { RoadmapRole, WorkspaceRole } from '@/_core/utils/roles';

export class RoadmapsQuery extends Query<RoadmapsQueryResult> {
    constructor(public ProductSlug: string) {
        super('RoadmapsQuery');
    }
}
export class RoadmapQuery extends Query<RoadmapQueryResult> {
    constructor(public RoadmapId: string) {
        super('RoadmapQuery');
    }
}

export class CreateRoadmapQuery extends Query<CreateRoadmapQueryResult> {
    constructor(public ProductSlug: string) {
        super('CreateRoadmapQuery');
    }
}

export class GetSelectableStorymapsForRoadmapQuery extends Query<GetSelectableStorymapsForRoadmapQueryResult> {
    constructor(public RoadmapId: string) {
        super('GetSelectableStorymapsForRoadmapQuery');
    }
}

export interface GetSelectableStorymapsForRoadmapQueryResult {
    Storymaps: SimpleStorymapModel[];
}

export class ReleaseRoadmapSettingsQuery extends Query<ReleaseRoadmapSettingsQueryResult> {
    constructor(public RoadmapId: string) {
        super('ReleaseRoadmapSettingsQuery');
    }
}

export interface ReleaseRoadmapSettingsQueryResult {
    SelectableStorymaps: SimpleStorymapModel[];
    LinkedStorymapIds: string[];
}

export class EpicRoadmapSettingsQuery extends Query<EpicRoadmapSettingsQueryResult> {
    constructor(public RoadmapId: string) {
        super('EpicRoadmapSettingsQuery');
    }
}

export interface EpicRoadmapSettingsQueryResult {
    SelectableStorymaps: SimpleStorymapModel[];
    LinkedStorymapIds: string[];
}

export class RoadmapCardDetailsQuery extends Query<RoadmapCardDetailsQueryResult> {
    constructor(public RoadmapId: string, public Id: string) {
        super('RoadmapCardDetailsQuery');
    }
}

export class GetReleasesForStorymapQuery extends Query<GetReleasesForStorymapQueryResult> {
    constructor(public RoadmapId: string, public StorymapId: string) {
        super('GetReleasesForStorymapQuery');
    }
}

export class RoadmapShareDialogQuery extends Query<RoadmapShareDialogQueryResult> {
    constructor(public RoadmapId: string) {
        super('RoadmapShareDialogQuery');
    }
}

export class GetEpicsForStorymapQuery extends Query<GetEpicsForStorymapQueryResult> {
    constructor(public RoadmapId: string, public StorymapId: string) {
        super('GetEpicsForStorymapQuery');
    }
}

export interface GetEpicsForStorymapQueryResult {
    Activities: EpicItemModel[];
}

export interface EpicItemModel {
    Id: string;
    Title: string;
    Tasks: EpicItemModel[];
}

export interface WorkspaceMemberModel {
    MemberId: string;
    Name: string;
    IsInInvitedState: boolean;
    IsMe: boolean;
    WorkspaceRole: WorkspaceRole;
    CollaboratorRole?: RoadmapRole;
}

export interface RoadmapShareDialogQueryResult {
    AddedWorkspaceMembers: WorkspaceMemberModel[];
    AllWorkspaceMembers: WorkspaceMemberModel[];
}

export interface GetReleasesForStorymapQueryResult {
    Releases: SimpleReleaseModel[];
}

export interface SimpleReleaseModel {
    Id: string;
    Name: string;
    IsArchived: boolean;
    IsReleased: boolean;
}

export interface RoadmapCardDetailsQueryResult {
    Description: string;
    Storymaps: SimpleStorymapModel[];
    Releases: SimpleReleaseModel[];
    LinkedReleases: LinkedReleaseModel[];
    LinkedSubtasks: LinkedSubtaskModel[];
    StatusReports: StatusReportDataModel[];
    Epic: LinkedEpicModel;
    Attachments: AttachmentModel[];
    WebLinks: WebLinkModel[];
    Comments: CommentModel[];
}

export interface StatusReportDataModel {
    Name: string;
    StatusKey: number;
    Category: StatusCategory;
    NumberOfCards: number;
    EstimationSum: number;
    Color: string;
}

export interface LinkedReleaseModel {
    Id: string;
    Title: string;
    Description: string;
    StorymapId: string;
    StatusReport: StatusReport;
    //other fields ... not used explicitly and thus not included
}

export interface LinkedSubtaskModel {
    Id: string;
    Title: string;
    StorymapId: string;
    //other fields ... not used explicitly and thus not included
}

export interface LinkedEpicModel {
    Title: string;
    Description: string;
    StorymapId: string;

    Releases: SimpleReleaseModel[];
    StatusReports: StatusReportDataModel[];
}

export interface SimpleReleaseModel {
    Id: string;
    Title: string;
}

export interface CreateRoadmapQueryResult {
    Storymaps: SimpleStorymapModel[];
}

export interface SimpleStorymapModel {
    Id: string;
    Title: string;
    Description: string;
    Slug: string;
}

export interface RoadmapsQueryResult {
    Roadmaps: RoadmapModel[];
}

export interface RoadmapModel {
    Id: string;
    Name: string;
    Type: RoadmapType;

    IsCurrentMemberCollaborator: boolean;
    IsCurrentMemberViewer: boolean;

    CreatedAt: string;
    LastActivityAt: string;
}

export enum RoadmapType {
    ReleaseRoadmap = 1,
    EpicRoadmap = 2,

    PortfolioRoadmap = 3,
    HighLevelRoadmap = 4,

    IdeaRoadmap = 5,

    FreeRoadmap = 6,
}

export enum PrioFrameworkType {
    ValueEffort = 0,
    RICE = 1,
    KANO = 2,
}

export interface RoadmapQueryResult {
    ProductSlug: string;
    Id: string;
    Name: string;
    Type: RoadmapType;
    StorymapIds: string[];
    IsMissingStorymap: boolean;

    Columns: RoadmapResultColumn[];
    Swimlanes: RoadmapResultSwimlane[];
    Cards: RoadmapResultCard[];
    Labels: RoadmapResultCardLabel[];
    WorkspaceMembers: RoadmapResultWorkspaceMember[];
    Storymaps: SimpleStorymapModel[];
    PrioFrameworkType: PrioFrameworkType;
    ShouldHidePrioScores: boolean;
    ShouldShowPrioOnboarding: boolean;
    CollaboratorRole: RoadmapRole;
}

interface RoadmapResultWorkspaceMember {
    Id: string;
    UserId: string;
    Role: WorkspaceRole;
    Name: string;
}

interface RoadmapResultCardLabel {
    Id: string;
    Name: string;
    Color: RoadmapCardLabelColor;
    Order: number;
}

export enum RoadmapCardLabelColor {
    NotDefined,

    Blue,
    LightBlue,
    Red,
    Yellow,
    Orange,
    Green,
    Purple,
    Pink,
    Grey,
}

export enum RoadmapPrioCategoryLabelColor {
    Blue,
    Orange,
    Green,
    Pink,
}

interface RoadmapResultColumn {
    Id: string;
    Name: string;
    Order: number;
}

interface RoadmapResultSwimlane {
    Id: string;
    Name: string;
    Order: number;
}

interface RoadmapResultCard {
    Id: string;
    Title: string;
    Order: number;
    ColumnId: string;
    SwimlaneId: string;
    AttachmentCount: number;
    StatusReport: StatusReport;
    EpicStatusReport: EpicStatusReport;
    LabelIds: string[];
    IsArchived: boolean;
    ArchivedAt: string;
    CreatedAt: string;
    CreatedBy: string;

    ValueEffortValue: number;
    ValueEffortEffort: number;
    ValueEffortScore: number;

    RiceReach: number;
    RiceEffort: number;
    RiceConfidence: number;
    RiceImpact: number;
    RiceScore: number;

    HasComment: boolean;
    HasDescription: boolean;
}

export interface EpicStatusReport {
    StatusCategories: StatusCategory[];
    NumberOfUnestimatedCards: number;
    ReleaseIds: string[];
    NumberOfCardsInReleases: number;
    NumberOfUnscheduledCards: number;
    ActivityId: string;
    TaskId: string;
    EstimationUnit: string;
}

export interface StatusReport {
    StatusCategories: StatusCategory[];
    NumberOfUnestimatedCards: number;
    EstimationUnit: string;
    StartDate: string;
    ReleaseDate: string;
    ReleaseId: string;
    NumberOfReleases: number;
}

interface StatusCategory {
    Category: number;
    NumberOfCards: number;
    EstimationSum: number;
}

export interface CommentModel {
    Id: string;
    Comment: string;
    CreatedAt: string;
    CreatedBy: string;
    CreatedById: string;
}
